import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2/dist/sweetalert2.js';

@Component({
    selector: 'app-pb-event-application-form',
    templateUrl: './pb-event-application-form.component.html',
    styleUrls: ['./pb-event-application-form.component.scss']
})
export class PbEventApplicationFormComponent implements OnInit {

    // Page Builder

    @Input() content: any = {};
    @Input() module: any = {};
    @Input() id: any;
    paddingTop: any = '';
    paddingBottom: any = '';
    items: any = [];

    // Form Content

    eventApplicationForm;
    firstName: any = '';
    lastName: any = '';
    emailAddress: any = '';
    phoneNumber: any = '';
    event: any = '';
    venue: any = '';
    session: any = '';
    subscribe: any = '';

    // Loader

    processing = false;
    success = false;

    // Collections from Catalyst

    eventTypes: any = [];
    venues: any = [];
    sessions: any = [];
    sessionsPlaceholder = 'None available';


    constructor(
        private cd: ChangeDetectorRef,
        private api: ApiService,
        private formBuilder: FormBuilder,
        private router: Router
    ) {

        this.eventApplicationForm = this.formBuilder.group({
            firstName: new FormControl('', Validators.compose([
                Validators.required,
                Validators.minLength(2),
                Validators.pattern('^[A-Za-z0-9ñÑáéíóúÁÉÍÓÚ ]+$')
            ])),
            lastName: new FormControl('', Validators.compose([
                Validators.required,
                Validators.minLength(2),
                Validators.pattern('^[A-Za-z0-9ñÑáéíóúÁÉÍÓÚ ]+$')
            ])),
            emailAddress: new FormControl('', Validators.compose([
                Validators.required,
                Validators.minLength(2),
                Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')
            ])),
            phoneNumber: new FormControl('', Validators.compose([
                Validators.required,
                Validators.minLength(9),
                Validators.pattern('^[0-9]*$'),
            ])),
            event: new FormControl('', Validators.compose([
                Validators.required
            ])),
            venue: new FormControl('', Validators.compose([
                Validators.required
            ])),
            session: new FormControl('', Validators.compose([
                Validators.required
            ])),
            subscribe: new FormControl('')
        });

    }

    ngOnInit(): void {

        this.content.forEach(element => {
            const item = {
                content: element.data,
                alignment: this.determineAlignment(element.alignment),
                padding: this.determinePadding(element.padding),
                type: element.item.type
            };

            this.items.push(item);
        });

        this.paddingTop = this.module.paddingTop; // Module Padding
        this.paddingBottom = this.module.paddingBottom;

        this.fetchEventTypes();

    }

    /**
     * Alignment is returned as LEFT, CENTER, RIGHT
     * Determine class based on response
     */
    determineAlignment(alignment) {

        if (alignment === 'LEFT') {
            return 'text-left';
        } else if (alignment === 'CENTER') {
            return 'text-center';
        } else if (alignment === 'RIGHT') {
            return 'text-right';
        }

    }

    /**
     * Padding is returned as SMALL, MEDIUM, LARGE, XLARGE
     * Determine pixels based on response
     */
    determinePadding(padding) {

        if (padding === 'SMALL') {
            return '10';
        } else if (padding === 'MEDIUM') {
            return '15';
        } else if (padding === 'LARGE') {
            return '30';
        } else if (padding === 'XLARGE') {
            return '60';
        }

    }

    /**
     * Populate payload
     */
    saveChanges() {

        if (!this.eventApplicationForm.valid) {
            Object.keys(this.eventApplicationForm.controls).forEach(field => {
                const control = this.eventApplicationForm.get(field);
                control.markAsTouched({ onlySelf: true });
            });

            return false;
        }

        let user: any = localStorage.getItem('user');
        let userAccountKey: any = '';

        if (user) {
            try {
                user = JSON.parse(user);
                userAccountKey = user.Result.UserAccountKey._text;
            } catch (err) { }
        }

        const frm = this.eventApplicationForm.getRawValue();
        const gaUserId = document.cookie.match(/_ga=(.+?)(?:;|$)/)?.[1].split('.').slice(-2).join('.');

        const data = {
            firstName: frm.firstName,
            lastName: frm.lastName,
            emailAddress: frm.emailAddress,
            phoneNumber: frm.phoneNumber,
            eventName: frm.event.EventTypeName._text,
            venueName: frm.venue.EventName._text,
            sessionNames: (frm.session ? frm.session : []),
            eventCatalyst: frm.event, // Complete catalyst object because we need to send the keys from the server
            venueCatalyst: frm.venue, // Complete catalyst object because we need to send the keys from the server
            sessionCatalyst: (frm.session ? frm.session : []), // Complete catalyst object because we need to send the keys from the server
            subscribe: frm.subscribe,

            // UTM Variables

            formName: 'Event Application',
            utmSource: sessionStorage.getItem('utmSource'),
            utmMedium: sessionStorage.getItem('utmMedium'),
            utmCampaign: sessionStorage.getItem('utmCampaign'),
            utmContent: sessionStorage.getItem('utmContent'),
            utmTerm: sessionStorage.getItem('utmTerm'),
            clientId: gaUserId,
            userId: userAccountKey
        };

        this.submitToApi(data);

    }

    /**
     * Submit payload to api
     * @param data: any
     */
    async submitToApi(data) {

        this.processing = true;
        this.success = false;

        const redirectUrl = this.module.prebuilt_module_data.redirect_url ? this.module.prebuilt_module_data.redirect_url : false;

        await this.api.submit_event_application_form_data(data)
            .subscribe(res => {
                this.processing = false;
                this.cd.detectChanges();

                this.eventApplicationForm.reset();
                this.success = true;

                if (redirectUrl) {
                    this.router.navigateByUrl(redirectUrl);
                }
            }, (err) => {
                this.processing = false;
                this.cd.detectChanges();

                Swal.fire({
                    title: 'Error',
                    text: 'Unable to send your message',
                    confirmButtonText: 'OK',
                    allowOutsideClick: false,
                    showCloseButton: true,
                    customClass: 'swal-ibtc',
                    position: 'top'
                }).then((result) => {
                    // Nothing needed
                });
            });

    }

    /**
     * Easier access to the controlname reference
     */
    get f() {

        return this.eventApplicationForm.controls;

    }

    /**
     * Fetch all courses.
     * This data is used in the course dropdown on the contact form
     */
    async fetchEventTypes() {

        await this.api.fetch_event_types()
            .subscribe(res => {

                // console.log('res', res);
                // console.log('id', this.id);

                const eventTypesData = res?.Result?.EventTypes?.EventType ?? [];
                this.eventTypes = this.ensureArray(eventTypesData);

                const index = this.eventTypes.findIndex(x => x?.EventTypeKey?._text === this.id);

                this.eventApplicationForm.patchValue({
                    event: this.eventTypes[index]
                });

                if (this.id != null){
                    this.fetchEvents(this.id);
                } else {
                    console.log('No event type key found.');
                }

                this.cd.detectChanges();
            }, (err) => {
                this.eventTypes = [];
                this.venues = [];
                this.sessions = [];
            });

    }

    /**
     * Fetch all venues based on event type key
     */
    async fetchEvents(eventKey) {

        await this.api.fetch_events(eventKey)
            .subscribe(res => {
                const venueData = res?.Result?.Events?.Event ?? [];
                this.venues = this.ensureArray(venueData);
                this.cd.detectChanges();
            }, (err) => {
                this.venues = [];
                this.sessions = [];
            });

    }

    /**
     * Fetch all sessions based on venue key
     */
    async fetchSessions(venueKey) {

        await this.api.fetch_event_sessions(venueKey)
            .subscribe(res => {
                console.log('sessions', res);

                this.sessions = this.ensureArray(res.Result?.EventSessions?.EventSession);

                if (this.sessions.length > 0) {
                    this.sessionsPlaceholder = 'Please select in order of preference';
                } else {
                    this.sessionsPlaceholder = 'None available';
                }

                this.cd.detectChanges();
            }, (err) => {
                this.sessions = [];
            });

    }

    /**
     * When event has been selected then fetch venues
     * @param evt: any
     */
    eventTypeChanged(evt) {

        this.venues = [];
        this.sessions = [];
        this.fetchEvents(evt.value.EventTypeKey._text);

    }

    /**
     * When venue has been selected then fetch the sessions
     * @param evt: any
     */
    venueChanged(evt) {

        this.sessions = [];
        this.fetchSessions(evt.value.EventKey._text);

    }

    /**
     * Changes catalyst response to always be an array
     * @param possibleArray: array
     * @returns: array
     */
    ensureArray(possibleArray) {
        if (possibleArray) {
            if (Array.isArray(possibleArray)) {
                return possibleArray;
            } else {
                return [possibleArray];
            }
        } else {
            return [];
        }
    }

}
